/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { CheckInComplianceDrillInModal } from "../check_in_compliance_drill_in_modal";
import { OrderNumberCell } from "../order_number_cell";
import camelize from "../../utils/camelize";
import { formatDate } from "../../utils/formatDate";

const EMPTY_DURATION = "—";

// Same columns as the Employee 360 orders table.
const COLUMN_OPTIONS = [
  { key: "location", sortable: true },
  { key: "order_id", sortable: true },
  { key: "order_no", label: "Order #", sortable: true },
  { key: "order_details", label: "Order Details", sortable: false },
  { key: "opened", sortable: true },
  { key: "no_of_guest", label: "# of Guests", sortable: true },
  { key: "discount_amount", sortable: true },
  { key: "amount", sortable: true },
  { key: "tax", sortable: true },
  { key: "tip", sortable: true },
  { key: "gratuity", sortable: true },
  { key: "total", sortable: true },
  { key: "paid", sortable: true },
  { key: "closed", sortable: true },
  { key: "duration", sortable: true },
  { key: "check_no", label: "Checks", sortable: true },
  { key: "tab_names", sortable: true },
];

const COLUMNS = COLUMN_OPTIONS.map((column) => ({
  key: column.key,
  label: column.label || camelize(column.key),
  sortable: column.sortable,
}));

const formatAmount = (amount) => (amount == null ? "0.00" : Number(amount).toFixed(2));

const formatDuration = (seconds) => {
  if (seconds == null || Number(seconds) < 0) return EMPTY_DURATION;

  const total = Number(seconds);
  return [Math.floor(total / 3600), Math.floor((total % 3600) / 60), total % 60]
    .map((part) => part.toString().padStart(2, "0"))
    .join(":");
};

const buildRow = (record, onViewOrder) => ({
  location: record?.location,
  order_id: record?.order_id,
  order_no: record?.order_no,
  order_details: <OrderNumberCell orderValue={record} onView={onViewOrder} />,
  opened: formatDate(record?.opened, record?.time_zone_abbr),
  no_of_guest: record?.no_of_guest,
  discount_amount: `$${formatAmount(record?.discount_amount)}`,
  amount: `$${formatAmount(record?.amount)}`,
  tax: `$${formatAmount(record?.tax)}`,
  tip: `$${formatAmount(record?.tip)}`,
  gratuity: `$${formatAmount(record?.gratuity)}`,
  total: `$${formatAmount(record?.total)}`,
  paid: formatDate(record?.paid, record?.time_zone_abbr),
  closed: formatDate(record?.closed, record?.time_zone_abbr),
  duration: formatDuration(record?.duration),
  check_no: record?.check_no,
  tab_names: record?.tab_names,
});

// Lists the orders behind one Check-In Compliance row's Eligible Orders count, under the report's applied filters.
export const EligibleOrderModal = (props) => (
  <CheckInComplianceDrillInModal
    {...props}
    kind="eligible_orders"
    title="Eligible Orders"
    testIdPrefix="eligible-order-modal"
    columns={COLUMNS}
    buildRow={buildRow}
    type="orders"
    errorMessage="Failed to load eligible orders."
  />
);
